import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { League } from '../../../sdk/model';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';
import {
  Box,
  Select,
  Dialog,
  DialogContent,
  MenuItem,
  Button,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Paper,
  Grid,
  Divider,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import TeamRosterCustom from './roster/TeamRosterCustom';
import TeamRosterMobile from './roster/TeamRosterMobile';
import { useTheme, useMediaQuery } from '@mui/material';
import TeamAssets from './TeamAssets';
import TeamHistory from './TeamHistory';
import { useAppSelector } from '../../../app/hooks';
import { usePlayerDataLoadFlat } from '../../util/PlayerDataUtil';
import TeamSalaryInfo from './TeamSalaryInfo';

function TeamView() {
  const {leagueId, teamId} = useParams();
  
  const navigate = useNavigate();

  // Redux store variables
  const user = useAppSelector((state) => state.user);
  const playerMap = usePlayerDataLoadFlat();

  console.log("Player Map: ", playerMap);
  
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [viewingImage, setViewingImage] = React.useState(false);
  const [view, setView] = React.useState('lineup');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const league : League|undefined = useLoadLeagueData({
    leagueId: leagueId, 
    userId: user.id, 
    loadBasicDraftData: false,
    loadFullDraftData: false, 
    loadContractData: true,
    loadBasicAuctionData: false, 
    loadFullAuctionData: false,
    loadRosterData: false, 
    loadRosterFor: [teamId],
    loadScheduleData: false, 
    loadWaivedContracts: false,
    isException, 
    setIsException, 
    isLoadComplete, 
    setLoadComplete
  });

  if (!isLoadComplete || !teamId || !league) {
    return <Typography color="white">Loading roster data..</Typography>
  }
  
  const thisTeam = league.teams.find(team => team.id === teamId)!;
  const teamMap = new Map(league.teams.map(team => [team.id, team]));
  
  const myTeam = thisTeam?.owners?.some(owner => owner.id === user.id)!;
  
  return (
    <Box sx={{ p: 2 }}>
      <Dialog open={viewingImage} maxWidth='md' fullWidth>
        <DialogContent>
          <Box position="absolute" top={8} right={8} zIndex={1}>
            <IconButton onClick={() => setViewingImage(false)}>
              <Close />
            </IconButton>
          </Box>
          <Box component="img" src={thisTeam.picture} sx={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }} />
        </DialogContent>
      </Dialog>

      {/* Team Header Section */}
      <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
        {/* League Name - Centered */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h5" color="primary" gutterBottom>
            {league?.leagueName}
          </Typography>
          
          {/* Team Selector - Centered below league name */}
          <Box display="flex" justifyContent="center" mb={3}>
            <Select
              value={teamId}
              onChange={(event) => navigate(`/league/${league?.id}/team/${event.target.value}`)}
              size="small"
              sx={{ minWidth: 250 }}
            >
              {league?.teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm="auto" sx={{ display: 'flex', alignItems: 'center', gap: 2, mx: 'auto' }}>
            <IconButton 
              onClick={() => setViewingImage(true)}
              disabled={!thisTeam.picture}
              sx={{ p: 0, width: 80, height: 80 }}
            >
              <Avatar 
                alt={thisTeam.name} 
                src={thisTeam.picture} 
                sx={{ width: '100%', height: '100%', fontSize: '2rem' }}
              >
                {!thisTeam.picture && thisTeam.name?.substring(0, 1).toUpperCase()}
              </Avatar>
            </IconButton>
            <Box textAlign={{ xs: 'center', sm: 'left' }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {thisTeam.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {thisTeam.owners?.map(owner => owner.username).join(', ')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Propose Trade Button - Centered below team info */}
        {!myTeam && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button 
              variant="contained" 
              color="primary"
              size="large"
              onClick={() => navigate(`/league/${league?.id}/trade?teamId=${teamId}`)}
            >
              Propose Trade
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Team Stats */}
        <TeamSalaryInfo salaryCap={league?.currentSeason?.nflSeason?.salaryCap} currentSeasonSalary={thisTeam.currentSeasonSalary} />
      </Paper>

      {/* Main Content Tabs */}
      <Paper elevation={2} sx={{ mt: 3 }}>
        <Tabs 
          value={view} 
          onChange={(event, value) => setView(value)}
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': {
              height: 4,
            },
          }}
        >
          <Tab label="Roster" value="lineup" />
          <Tab label="Contracts & Assets" value="assets" />
          <Tab label="Team History" value="history" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {view === 'lineup' && leagueId && thisTeam?.id && league?.currentSeasonYear && (
            isMobile ? (
              <TeamRosterMobile
                leagueId={leagueId}
                team={thisTeam}
                playerMap={playerMap}
                currentSeasonYear={league.currentSeasonYear}
                rosterSettings={new Map(Object.entries(league.rosterSettings || {})) as Map<string, number>}
                myTeam={myTeam}
              />
            ) : (
              <TeamRosterCustom 
                leagueId={leagueId} 
                team={thisTeam} 
                currentSeasonYear={league.currentSeasonYear}
                playerMap={playerMap} 
                rosterSettings={new Map(Object.entries(league.rosterSettings || {})) as Map<string, number>} 
                myTeam={myTeam} 
              />
            )
          )}
          {view === 'assets' && league && thisTeam && league.currentSeasonYear && teamMap && (
            <TeamAssets 
              league={league} 
              team={thisTeam} 
              currentSeason={league.currentSeasonYear} 
              playerMap={playerMap} 
              myTeam={myTeam} 
              teamMap={teamMap} 
            />
          )}
          {view === 'history' && thisTeam && league?.id && (
            <TeamHistory 
              team={thisTeam} 
              leagueId={league.id} 
            />
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default TeamView;
